import React from 'react';
import Image from 'next/image';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

const PaginationBlog: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  className = '',
}) => {
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5; // Maximum number of page buttons to show

    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push('...');
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('...');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className={`flex items-center justify-center gap-2 ${className}`}>
      {/* Previous Button */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="group flex h-10 w-10 items-center justify-center rounded-lg border border-secGrayBeta shadow-blogCard transition-all duration-300 hover:border-lightBlue hover:bg-lightBlue disabled:cursor-not-allowed disabled:opacity-70"
        aria-label="Previous page"
      >
        <Image
          src="/images/arrow-right.png"
          alt="Previous"
          width={16}
          height={16}
          className="rotate-90 transition-all duration-300 group-hover:brightness-0 group-hover:invert"
        />
      </button>

      {/* Page Numbers */}
      <div className="flex items-center gap-2">
        {pageNumbers.map((page, index) => {
          if (page === '...') {
            return (
              <span
                key={`ellipsis-${index}`}
                className="text-gray-500 flex h-10 w-10 items-center justify-center"
              >
                ...
              </span>
            );
          }

          const pageNum = page as number;
          const isActive = pageNum === currentPage;

          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              className={`flex h-10 w-10 items-center justify-center rounded-lg border font-medium transition-all duration-300 ${
                isActive
                  ? 'border-lightBlue bg-lightBlue text-white'
                  : 'hover:bg-gray-50 hover:border-gray-400 border border-secGrayBeta text-black shadow-blogCard'
              }`}
              aria-label={`Page ${pageNum}`}
              aria-current={isActive ? 'page' : undefined}
            >
              {pageNum}
            </button>
          );
        })}
      </div>

      {/* Next Button */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="group flex h-10 w-10 items-center justify-center rounded-lg border border-secGrayBeta shadow-blogCard transition-all duration-300 hover:border-lightBlue hover:bg-lightBlue disabled:cursor-not-allowed disabled:opacity-70"
        aria-label="Next page"
      >
        <Image
          src="/images/arrow-right.png"
          alt="Next"
          width={16}
          height={16}
          className="-rotate-90 transition-all duration-300 group-hover:brightness-0 group-hover:invert"
        />
      </button>
    </div>
  );
};

export default PaginationBlog;
